/**
 * <cart-upsell>
 * ---------------------------------------------------------------
 * Wraps the "Upgrade Now" button inside the cart drawer upsell card
 * (see snippets/cart-upsell.liquid).
 *
 * On click it:
 *   1. Removes the current (lower pack size) variant from the cart
 *   2. Adds the target (higher pack size) variant, quantity always 1
 *   3. Emits the same 'update:cart:drawer' event the rest of the
 *      theme's cart flow uses, so <cart-drawer> re-renders itself.
 *
 * Because the upsell card lives inside the cart-body zone (which is
 * part of that same re-render), the card re-evaluates its own
 * eligibility on that next render and hides itself automatically
 * once the upgraded variant is already in the cart.
 * No extra "hide the card" logic is needed here.
 */
class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector("[data-cart-upsell-upgrade]");
    this.handleUpgradeClick = this.handleUpgradeClick.bind(this);
  }

  connectedCallback() {
    if (this.button) {
      this.button.addEventListener("click", this.handleUpgradeClick);
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener("click", this.handleUpgradeClick);
    }
  }

  async handleUpgradeClick() {
    const lineKey = this.dataset.cartUpsellLineKey;
    const currentVariantId = this.dataset.cartUpsellCurrentVariantId;
    const targetVariantId = this.dataset.cartUpsellTargetVariantId;

    if ((!lineKey && !currentVariantId) || !targetVariantId) return;

    this.setLoading(true);

    try {
      // Step 1 — remove the lower pack size line (prefer line key)
      await fetch(window.routes.cart_change_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: lineKey || currentVariantId,
          quantity: 0,
          sections: "cart-drawer",
        }),
      });

      // Step 2 — add the upgraded (higher pack size) variant
      const addResponse = await fetch(window.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: parseInt(targetVariantId, 10), quantity: 1 }],
          sections: "cart-drawer",
        }),
      });

      if (!addResponse.ok) {
        throw new Error("Failed to add upgraded variant to cart");
      }

      const responseJson = await addResponse.json();

      if (window.eventBus) {
        window.eventBus.emit("update:cart:drawer", responseJson);
      }
    } catch (error) {
      console.error("Cart upsell upgrade failed:", error);
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (!this.button) return;
    this.button.disabled = isLoading;
    this.button.classList.toggle("loading", isLoading);
  }
}

customElements.define("cart-upsell", CartUpsell);
